import React, { useEffect, useRef, useState } from "react";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import ReCAPTCHA from "react-google-recaptcha";
import { apiUrl } from "../utils/apiBase";
import { isPrerendering } from "../utils/isPrerendering";

const jobs = [
  {
    title: "Automation Integration Specialist",
    location: "Location: Livonia, MI | Full-Time",
    description:
      "As an Automation Integration Specialist at MAG, you will lead the planning, coordination, and execution of controls-focused automation projects. Intralogistics automation using AMRs, conveyors, and integrated control systems is essential for scalable manufacturing environments. You'll manage software and AMR integration projects across design, development, deployment, and commissioning phases.",
    responsibilities: [
      "Manage multiple simultaneous software and AMR integration projects across all phases",
      "Define software architecture, infrastructure scope, timelines, and resource allocation",
      "Coordinate with software, controls, mechanical, and IT/OT teams",
      "Review and contribute to software system designs for AMR and fleet management integration",
      "Ensure proper use of communication protocols such as OPC UA, MQTT, and REST APIs",
      "Provide direction on architecture, data flow, debugging strategy, and cybersecurity",
      "Own software documentation including APIs, system diagrams, integration specs, and test plans",
      "Communicate project status, risks, and outcomes with customers, vendors, and internal leadership",
      "Ensure delivered software meets customer requirements and industry standards",
      "Serve as the primary technical point of contact for software and AMR integration scope",
    ],
    required: [
      "Bachelor's degree in computer, software, robotics, mechatronics, or related field (or equivalent experience)",
      "Proficiency in C/C++, Python, SQL, and JavaScript (React preferred)",
      "Strong understanding of software engineering fundamentals and industrial integration",
      "Proven ability to deliver complex technical projects in manufacturing or robotics environments",
      "Excellent communication, organization, and cross-functional collaboration skills",
    ],
    preferred: [
      "Experience with AMR platforms and Fleet Management Systems",
      "Familiarity with industrial protocols including Modbus and VDA5050",
      "Experience with CI/CD and containerized deployments",
      "Prior work in robotics integration, manufacturing automation, or warehouse logistics",
    ],
  },
  {
    title: "Automation Solutions Engineer",
    location: "Location: Livonia, MI | Full-Time",
    description:
      "As an Automation Solutions Engineer at MAG, you will be responsible for leading the planning, coordination, and execution of multiple automation projects and installations. Intralogistics automation using AMRs, conveyors, and integrated control systems is at the heart of scalable, efficient manufacturing environments. This position requires a strong command of engineering principles to ensure all technical teams deliver projects accurately, efficiently, on schedule, and within budget. You'll conduct frequent onsite facility evaluations, develop practical solutions for AMR integration challenges, and serve as the primary technical point of contact for all AMR installation and integration activities within assigned projects.",
    responsibilities: [
      "Lead and manage multiple concurrent automation and AMR integration projects through all phases (design, development, deployment, commissioning)",
      "Define and oversee technical designs, installation scopes, project timelines, and resource allocation",
      "Coordinate efforts across cross-functional teams (software engineering, controls, mechanical design, IT/OT)",
      "Independently determine project scope, develop preliminary plans, and determine project specifications",
      "Conduct frequent onsite facility evaluations to gather field data and assess operational conditions",
      "Identify potential challenges to AMR integration and develop practical solutions",
      "Review and contribute to system designs involving AMR integrations and fleet management systems",
      "Ensure installation procedures and mechanical layouts align with customer expectations",
      "Lead development, organization, and maintenance of all project documentation (scopes of work, schedules, installation specs, test plans)",
      "Maintain clear communication with customers, vendors, and internal leadership",
      "Prepare and present detailed technical documentation and reports on project status and outcomes",
      "Ensure all systems comply with relevant industry standards, safety regulations, and customer specifications",
      "Lead design reviews, system testing, and post-project evaluations for continuous improvement",
    ],
    required: [
      "Bachelor's degree in engineering, robotics engineering, or equivalent hands-on experience in industrial automation or robotics integration",
      "Strong understanding of mechanical design principles, machine integration, and industrial automation systems",
      "Proficiency in 3D CAD software (SolidWorks, AutoCAD, or Inventor) for creating mechanical layouts and fabrication drawings",
      "Proficiency in Microsoft Excel for data analysis, project tracking, and technical documentation",
      "Demonstrated ability to lead and deliver complex mechanical or automation projects from design through commissioning",
      "Working knowledge of mechanical fabrication methods, fastening systems, tolerancing, and material selection",
      "Proven ability to troubleshoot and resolve mechanical or system-level issues",
      "Excellent communication, organization, and collaboration skills",
      "Ability to walk 10,000 steps a day, work standing all day, travel 30-50%, and lift up to 50 pounds",
    ],
    preferred: [
      "Experience in robotic or AMR integration, including mounting design, cart interfaces, and safety scanner functionality",
      "Familiarity with industrial standards and safety regulations (ANSI/RIA, ISO 3691, or equivalent)",
      "Experience developing installation specifications, test plans, and commissioning documentation",
      "Prior exposure to manufacturing or warehouse automation projects",
      "Hands-on familiarity with construction processes, mechanical assembly, and field installations",
    ],
  },
  {
    title: "Fabricator / Welder",
    location: "Location: Livonia, MI | Part-Time with Opportunity to Transition to Full-Time",
    description:
      "As a Fabricator / Welder at MAG, you will support the fabrication and assembly of custom components used in manufacturing automation and robotics projects. This role is built for someone looking to grow welding and fabrication experience in a hands-on shop. It starts as a part-time position, with the opportunity to move to full-time based on performance and business needs.",
    responsibilities: [
      "Perform MIG and TIG welding on steel, stainless steel, and aluminum components",
      "Fabricate parts and assemblies according to drawings, measurements, and project requirements",
      "Cut, grind, drill, fit, and prepare materials for welding and assembly",
      "Read and interpret basic fabrication drawings and measurements",
      "Assist with building frames, brackets, carts, guards, and other custom components",
      "Inspect completed welds and fabricated parts for quality and accuracy",
      "Maintain welding equipment, tools, and a clean, organized work area",
      "Work closely with MAG engineering and automation teams on custom projects",
      "Follow required safety procedures and shop practices",
    ],
    required: [
      "Basic experience or training in MIG and/or TIG welding",
      "Ability to safely use common fabrication and shop tools",
      "Ability to accurately measure, cut, and fit materials",
      "Strong attention to detail and workmanship",
      "Dependable, willing to learn, and able to work independently and as part of a team",
    ],
    preferred: [
      "Welding or fabrication coursework, certification, or technical school experience",
      "Experience with both MIG and TIG welding",
      "Experience welding steel, stainless steel, and aluminum",
      "Ability to read fabrication drawings and blueprints",
      "Previous experience in a fabrication, manufacturing, automotive, or automation environment",
    ],
  },
  {
    title: "Mechatronics Engineer",
    location: "Location: Livonia, MI | Full-Time",
    description:
      "As a Mechatronics Engineer at MAG, you will design new mechanical parts and assemblies and integrate them with existing electronic, sensor, and computing components to produce complete, field-ready systems. You will carry designs from concept through CAD, prototyping, and validation, specifying the motors, sensors, and electrical interfaces each assembly requires and working with controls, software, and manufacturing teams to bring them into production.",
    responsibilities: [
      "Design new mechanical parts, weldments, brackets, enclosures, and assemblies in 3D CAD, and produce fabrication-ready drawings with appropriate GD&T",
      "Integrate mechanical designs with existing electronic, sensor, and computer hardware, including mounting, alignment, cable routing, connector selection, and thermal and ingress-protection considerations",
      "Select and specify motors, drives, actuators, gearboxes, bearings, and mechanical power transmission components based on load, duty cycle, and accuracy requirements",
      "Specify sensors — LiDAR, encoders, proximity and photoelectric sensors, cameras, IMUs, safety scanners — including placement, field-of-view and mounting tolerance, and interface requirements",
      "Define electrical interface requirements (voltage, current draw, signal type, connector pinout, grounding and shielding) and coordinate with electrical and controls engineers on implementation",
      "Build, test, and iterate on prototypes; develop test plans and validate designs against functional, environmental, and safety requirements",
      "Perform engineering calculations and analysis (structural, stiffness, tolerance stack-up, kinematics, power budget) to support design decisions",
      "Apply design-for-manufacturing and design-for-assembly principles; work with machine shops, fabricators, and suppliers on part sourcing and producibility",
      "Create and maintain BOMs, part numbers, drawing packages, and revision-controlled design documentation",
      "Ensure designs comply with relevant robotics and machinery standards, safety regulations, and customer specifications",
      "Support field installation, commissioning, and troubleshooting of designed hardware; capture lessons learned and feed them back into the design",
      "Participate in and lead design reviews, and contribute to continuous improvement of design standards and reusable components",
    ],
    required: [
      "Bachelor's degree (or pursuing) in mechatronics, robotics, mechanical engineering, or similar degree, or equivalent hands-on experience designing electromechanical systems",
      "Strong understanding of mechanical design principles, machine design, and electromechanical integration",
      "Proficiency in 3D CAD software (SolidWorks, AutoCAD, Inventor, NX, etc.) for part and assembly modeling, layouts, and fabrication drawings",
      "Working knowledge of mechanical fabrication methods, fastening systems, tolerancing and GD&T, and material selection",
      "Practical understanding of electrical fundamentals sufficient to specify sensors, actuators, power distribution, and wiring interfaces, and to read schematics and wiring diagrams",
      "Familiarity with robotics and machinery safety standards (ANSI/RIA R15.06, ISO 3691-4, ISO 10218, ISO 13849, or equivalent)",
      "Proven ability to troubleshoot and resolve mechanical, electrical, and system-level issues on real hardware",
      "Proficiency in Microsoft Excel for data analysis, calculations, and technical documentation",
      "Excellent communication, organization, and collaboration skills",
    ],
    preferred: [
      "Experience with AMR, AGV, or mobile robot hardware, including chassis, drive systems, cart interfaces, mounting design, and safety scanners",
      "Experience performing FEA, tolerance stack-up analysis, or motion/kinematic simulation",
      "Familiarity with PLCs, microcontrollers, or embedded platforms, and with industrial communication protocols",
      "Experience developing test plans, validation procedures, and commissioning documentation",
      "Working knowledge of PDM/PLM systems and formal revision control for drawing and BOM release",
      "Prior exposure to manufacturing or warehouse automation projects",
      "Hands-on familiarity with mechanical assembly, machine build, and field installation",
    ],
  },
];

const whyMag = [
  "Work on AMR and AGV launches in North American plants.",
  "Own a piece of the job: design, integration, commissioning, or support.",
  "Sit with plant teams, not just a slide deck.",
  "Learn the difference between a demo and a system that holds takt.",
  "Join a small team that still shows up on the floor after go-live.",
];

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [selectedPosition, setSelectedPosition] = useState(jobs[0].title);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [mounted, setMounted] = useState(false);
  const recaptchaRef = useRef(null);
  useEffect(() => {
    if (isPrerendering()) return;
    setMounted(true);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("position", selectedPosition);
      if (!captchaToken) {
        setStatus({ type: "error", message: "Please complete the captcha." });
        setSubmitting(false);
        return;
      }
      data.append("captcha", captchaToken);
      if (resume) data.append("resume", resume);

      const res = await fetch(apiUrl("/api/careers/apply"), {
        method: "POST",
        body: data,
      });

      if (!res.ok) throw new Error("Failed to send");
      setStatus({ type: "success", message: "Application sent. Thank you!" });
      if (window.gtag) {
        window.gtag("event", "submit_application", { form_name: "careers" });
      }
      setForm({ name: "", email: "", phone: "" });
      setResume(null);
      e.target.reset();
      setCaptchaToken("");
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (err) {
      setStatus({ type: "error", message: "Could not send application. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-950 text-white">
      <Seo
        title="Careers at MAG | AGV and AMR Integration, Metro Detroit"
        description="Join a 30+ person in-house team engineering, integrating, and supporting AMR and AGV fleets on demanding manufacturing floors. Based in Metro Detroit."
        path="/careers"
        jsonLd={breadcrumbList([
          { name: "Home", path: "/" },
          { name: "Careers", path: "/careers" },
        ])}
      />

      <section className="flex min-h-[calc(100svh-4.75rem)] flex-col">
        <div className="bg-black px-5 pt-10 sm:px-8 lg:px-12 xl:px-16">
          <h1 className="max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl xl:leading-[0.95]">
            Careers in AMR & AGV Automation
          </h1>
          <p className="mt-6 max-w-2xl pb-2 text-base leading-relaxed text-white/70 sm:text-lg">
            Join Manufacturing Automation Group to build modern automation systems with an impact.
          </p>
        </div>

        <div className="grid flex-1 lg:grid-cols-2">
          <div className="flex flex-col px-5 py-10 sm:px-8 lg:px-12 xl:px-16">
            <p className="page-eyebrow-on-dark">Apply</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Quick Application</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60">{selectedPosition}</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="name" className="sr-only">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  required
                  onChange={handleChange}
                  value={form.name}
                  className="field-input-on-dark"
                  placeholder="Name"
                />
              </div>
              <div>
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  onChange={handleChange}
                  value={form.email}
                  className="field-input-on-dark"
                  placeholder="Email"
                />
              </div>
              <div>
                <label htmlFor="phone" className="sr-only">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  required
                  onChange={handleChange}
                  value={form.phone}
                  className="field-input-on-dark"
                  placeholder="Phone"
                />
              </div>
              <div>
                <label htmlFor="resume" className="sr-only">
                  Resume (PDF/DOC up to 10MB)
                </label>
                <label
                  htmlFor="resume"
                  className="flex cursor-pointer items-center justify-between border-b border-white/25 py-2.5 text-base text-white/35 hover:text-white/70"
                >
                  <span className={resume ? "text-white" : ""}>
                    {resume ? resume.name : "Resume (PDF/DOC up to 10MB)"}
                  </span>
                </label>
                <input
                  id="resume"
                  type="file"
                  name="resume"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                  className="sr-only"
                />
              </div>
              {mounted && process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  theme="dark"
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token || "")}
                />
              ) : null}
              <div className="flex flex-col items-start gap-3 pt-2 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-60 sm:w-auto sm:min-w-[12rem]"
                >
                  {submitting ? "Sending..." : "Submit Application"}
                </button>
                {status ? (
                  <span
                    className={`text-sm font-medium ${
                      status.type === "success" ? "text-white" : "text-red-400"
                    }`}
                  >
                    {status.message}
                  </span>
                ) : null}
              </div>
            </form>
          </div>

          <div className="order-first flex flex-col bg-black px-5 py-10 sm:px-8 lg:order-none lg:px-12 xl:px-16">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Open roles">
              {jobs.map((item) => {
                const on = item.title === selectedPosition;
                return (
                  <button
                    key={item.title}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setSelectedPosition(item.title)}
                    className={
                      on
                        ? "border border-white bg-white px-4 py-2.5 text-left text-sm font-semibold text-gray-900"
                        : "border border-white/35 bg-transparent px-4 py-2.5 text-left text-sm font-medium text-white hover:border-white hover:bg-white/10"
                    }
                  >
                    {item.title}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 min-h-0 flex-1 overflow-y-auto pr-1">
              {jobs.map((item) => {
                const on = item.title === selectedPosition;
                return (
                  <div
                    key={item.title}
                    className={on ? "max-h-none opacity-100" : "max-h-0 overflow-hidden opacity-0"}
                  >
                    {item.location ? <p className="text-sm text-white/55">{item.location}</p> : null}
                    <h3 className={`${item.location ? "mt-5" : "mt-0"} text-sm font-semibold uppercase tracking-[0.18em] text-white/45`}>
                      Description
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{item.description}</p>
                    <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.18em] text-white/45">
                      Key Responsibilities
                    </h3>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-white/70">
                      {item.responsibilities.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.18em] text-white/45">
                      Qualifications
                    </h3>
                    <p className="mt-2 text-sm font-medium text-white">Required:</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm leading-relaxed text-white/70">
                      {item.required.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <p className="mt-4 text-sm font-medium text-white">Preferred:</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm leading-relaxed text-white/70">
                      {item.preferred.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 px-5 py-8 sm:px-8 lg:px-12 xl:px-16">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">Why MAG?</p>
          <ol className="mt-5 grid gap-6 lg:grid-cols-5">
            {whyMag.map((item, index) => (
              <li key={item} className="text-sm leading-relaxed text-white/75">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-white/40">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="mt-2 block">{item}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}
